/**
 * admin-ops-api の Workspaces (OPS-WS-1〜4) 用 fetch wrapper。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * 設計: spec/ai-inbox-core/admin-ops/design.md §5.2
 *
 * 守るライン: 受信レスポンスに PII を含めない（API 側で集約済）。クライアントは
 * 集約値のみを描画する。
 */

export type BillingStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

export type HealthFilterKey =
  | 'past_due'
  | 'inactive_14d_ai_off'
  | 'trial_no_lead_7d'
  | 'no_channel'
  | 'no_lead_30d';

export type WorkspaceSortKey = 'lastLogin' | 'createdAt' | 'leadsLast7d' | 'leadsLast30d';

/** #1886: Owner の「製品に関するお知らせメール」の許可状況（Owner の誰かが許可 = granted）。 */
export type OwnerProductEmailConsent = 'granted' | 'revoked' | 'unanswered';

export interface WorkspaceHealthRow {
  workspaceId: string;
  displayName: string;
  slug: string;
  billingStatus: BillingStatus;
  lastLoginAt: string | null;
  aiEnabled: boolean;
  channelCount: number;
  leadsLast7d: number;
  leadsLast30d: number;
  aiAdoptionRate: number | null;
  createdAt: string;
  ownerProductEmailConsent: OwnerProductEmailConsent;
  /** 許可状況の最終変更日時（ISO8601 / null = 変更なし） */
  ownerProductEmailConsentChangedAt: string | null;
  healthSignals: HealthFilterKey[];
}

export interface WorkspaceListResult {
  rows: WorkspaceHealthRow[];
  total: number;
  page: number;
  pageSize: number;
  billingDistribution: Record<BillingStatus, number>;
  sourceStatus: 'ok' | 'skipped' | 'error';
}

export interface WorkspaceListParams {
  status?: BillingStatus;
  health?: HealthFilterKey[];
  q?: string;
  sort?: WorkspaceSortKey;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/**
 * 本番は Pages と Worker が同一オリジン（`/api/*` を Worker に route）で baseUrl が空になるため、
 * `new URL()` ではなく文字列連結で組み立てる（空の base だと Invalid URL になる。audit-logs-api と同じ形）。
 */
export function buildWorkspaceListUrl(baseUrl: string, params: WorkspaceListParams): string {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.health) {
    for (const h of params.health) sp.append('health', h);
  }
  if (params.q) sp.set('q', params.q);
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  if (params.page) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  const qs = sp.toString();
  return `${baseUrl.replace(/\/$/, '')}/api/v1/workspaces${qs ? `?${qs}` : ''}`;
}

export async function fetchWorkspaces(
  baseUrl: string,
  params: WorkspaceListParams,
): Promise<WorkspaceListResult> {
  const url = buildWorkspaceListUrl(baseUrl, params);
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`admin-ops API /workspaces failed: ${res.status}`);
  }
  return (await res.json()) as WorkspaceListResult;
}

/** URL クエリパラメータ ↔ WorkspaceListParams の双方向変換 */
export function paramsFromUrlSearch(sp: URLSearchParams): WorkspaceListParams {
  const status = sp.get('status');
  const health = sp.getAll('health') as HealthFilterKey[];
  const q = sp.get('q') ?? undefined;
  const sort = sp.get('sort') as WorkspaceSortKey | null;
  const order = sp.get('order') as 'asc' | 'desc' | null;
  const page = sp.get('page');
  const pageSize = sp.get('pageSize');
  return {
    status: (status as BillingStatus | null) ?? undefined,
    health: health.length > 0 ? health : undefined,
    q,
    sort: sort ?? undefined,
    order: order ?? undefined,
    page: page ? parseInt(page, 10) : undefined,
    pageSize: pageSize ? parseInt(pageSize, 10) : undefined,
  };
}

export function paramsToUrlSearch(params: WorkspaceListParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.health) {
    for (const h of params.health) sp.append('health', h);
  }
  if (params.q) sp.set('q', params.q);
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  if (params.page && params.page > 1) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  return sp;
}
